import { useEffect, useRef, useState } from 'react';

export default function SolutionSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="solution" ref={ref} className="py-16 md:py-24 bg-[#050608] relative overflow-hidden">
      {/* Blue glow center */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-[#1E6BFF]/8 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <div
          className={`text-center mb-10 md:mb-16 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
            <span className="text-[#5A9AFF] text-xs font-bold tracking-[0.25em] uppercase">The Solution</span>
            <span className="h-0.5 w-10 bg-[#1E6BFF]" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#E8EFFF] mb-4 leading-tight">
            医師監修・特許成分<br />
            <span className="bg-gradient-to-r from-[#5A9AFF] to-[#1E6BFF] bg-clip-text text-transparent">GAMMAミネラル技術</span>
          </h2>
          <p className="text-[#C0CBDD] text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            フィジオショットが搭載する「GAMMA技術」は、
            高濃度ミネラルを輻射する新しいアプローチ方法です
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center mb-12 md:mb-16">
          <div
            className={`relative transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden w-full h-64 md:h-[440px] bg-[#0D1020] border border-[#1A2545] shadow-[0_0_40px_rgba(30,107,255,0.1)]">
              <img
                src="https://static.readdy.ai/image/1153be8c8a2f65c98102af48ca4fb482/06172a3fcd119d5060a770f6c4aa1da3.png"
                alt="GAMMAミネラル技術"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050608]/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#1E6BFF]/8 via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-4 right-4 bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] text-white rounded-xl px-4 py-3 shadow-[0_0_20px_rgba(30,107,255,0.6)]">
              <p className="text-xs font-black tracking-wider">KOREAN PATENT</p>
              <p className="text-xs font-semibold">韓国特許取得済み</p>
            </div>
          </div>

          <div
            className={`space-y-5 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div>
              <h3 className="text-2xl font-black text-[#E8EFFF] mb-3">GAMMAとは何か？</h3>
              <p className="text-[#C0CBDD] leading-relaxed mb-5">
                GAMMAとは、生体に必要な高濃度ミネラルを特定の周波数で輻射する技術
              </p>
            </div>

            <div className="space-y-3">
              {[
                {
                  step: '01',
                  title: '輻射によるミネラル浸透',
                  desc: '高濃度GAMMAミネラルが皮膚を浸透',
                },
                {
                  step: '02',
                  title: 'ステロイド不使用',
                  desc: '重金属・有毒性・ステロイド不使用',
                },
                {
                  step: '03',
                  title: '医師監修',
                  desc: '韓国病理医が監修し、基礎研究実施',
                },
              ].map((step) => (
                <div key={step.step} className="flex gap-4 bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/40 rounded-xl p-4 transition-colors">
                  <div className="bg-gradient-to-b from-[#2A7FFF] to-[#0D4FCC] bg-clip-text text-transparent font-black text-2xl w-10 shrink-0">{step.step}</div>
                  <div>
                    <p className="text-[#E8EFFF] font-bold text-sm mb-1">{step.title}</p>
                    <p className="text-[#D0DDF0] text-xs leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`grid sm:grid-cols-3 gap-4 md:gap-6 transition-all duration-700 delay-400 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            {
              icon: 'ri-fire-line',
              number: '可動域',
              title: '関節可動域や姿勢の変化へ',
              desc: '硬くなった素子へのアプローチで様々な効果を及ぼす可能性があります',
            },
            {
              icon: 'ri-run-line',
              number: '筋回復',
              title: '筋肉痛へのアプローチ',
              desc: '筋肉への違和感の持続時間と強度を大幅に短縮',
            },
            {
              icon: 'ri-flashlight-line',
              number: '即効性',
              title: '施術直後から実感',
              desc: '1回の施術から体感変化。多くのアスリートが初回で違いを感じている',
            },
          ].map((card) => (
            <div key={card.title} className="bg-[#0D1020] border border-[#1A2545] hover:border-[#1E6BFF]/50 rounded-xl p-6 text-center transition-all hover:shadow-[0_0_20px_rgba(30,107,255,0.1)] group">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A1535] border border-[#1A2F60] rounded-xl mx-auto mb-4 group-hover:border-[#1E6BFF]/50 transition-colors">
                <i className={`${card.icon} text-[#1E6BFF] text-2xl`} />
              </div>
              <div className="text-[#5A9AFF] font-black text-xs tracking-wider mb-1 uppercase">{card.number}</div>
              <h4 className="text-[#E8EFFF] font-bold text-sm mb-2">{card.title}</h4>
              <p className="text-[#D0DDF0] text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
